import { MessageCircle, type LucideIcon } from 'lucide-react';
import type { PlanModule } from '@tailoring-crm/shared';

/**
 * The outside services this shop can be wired to, as the INTEGRATIONS SCREEN speaks of them.
 *
 * This is presentation vocabulary — an icon, three copy keys and the plan module behind the
 * entry — so it lives in the web app rather than `@tailoring-crm/shared`. The API never reasons
 * about it: it refuses on the module alone, which is the one field here it also knows about.
 *
 * It is a list rather than a hand-written card because the second integration should be an
 * entry, not a rewrite of the screen. There is exactly one today.
 */

/**
 * The integrations that exist, as a union rather than `string`.
 *
 * Widening it is how a second integration announces itself: adding a key makes every switch
 * over an integration fail to compile until it has been thought about, which is the opposite
 * of a new card quietly falling through an `else`.
 */
export type IntegrationKey = 'whatsapp';

export interface IntegrationDefinition {
  key: IntegrationKey;
  icon: LucideIcon;
  /** What the shop calls the service — "WhatsApp", never the platform behind it. */
  titleKey: string;
  /** Who actually delivers it, read under the title. */
  providerKey: string;
  descriptionKey: string;
  /** The plan module the card locks against. The API refuses writes on the same one. */
  module: PlanModule;
}

export const INTEGRATIONS: readonly IntegrationDefinition[] = Object.freeze([
  {
    // The same icon the Notifications row in the settings hub already wears, so the screen a
    // tailor is being sent to looks like the one they were looking at.
    key: 'whatsapp',
    icon: MessageCircle,
    titleKey: 'integrations.whatsapp.title',
    providerKey: 'integrations.whatsapp.provider',
    descriptionKey: 'integrations.whatsapp.description',
    module: 'relay_whatsapp',
  },
] as const);
